/**
 * Vault reveal helpers.
 *
 * Vaulted records carry no plaintext: their `title` and `content` are empty on
 * the wire and everything readable lives inside the ciphertext. So a vaulted
 * note renders blank until it is decrypted — including its row in the list.
 */

import type { NoteRecord } from '../sync/dto.js';

export type DecryptedMap = Map<string, { title: string; content: string }>;

/**
 * Vaulted notes that still need decrypting.
 *
 * Used when the vault unlocks: rather than decrypting only the selected note
 * (which left the list blank and forced a manual reload to see anything), the
 * screen decrypts all of them.
 */
export function pendingVaultNotes(notes: NoteRecord[], decrypted: DecryptedMap): NoteRecord[] {
  return notes.filter(
    (note) =>
      note.isVaulted &&
      !note.deletedAt &&
      Boolean(note.vaultCiphertextBase64) &&
      !decrypted.has(note.id),
  );
}

/**
 * Returns notes with decrypted content substituted in for display.
 *
 * The stored records are never mutated — plaintext must not leak back into the
 * object that gets synced.
 */
export function withDecryptedContent(notes: NoteRecord[], decrypted: DecryptedMap): NoteRecord[] {
  return notes.map((note) => {
    if (!note.isVaulted) return note;
    const unlocked = decrypted.get(note.id);
    if (!unlocked) return note;
    return { ...note, title: unlocked.title, content: unlocked.content };
  });
}
